import React, { useState } from "react";
import { useRecoilState, useSetRecoilState } from "recoil";
import { nanoid } from "nanoid";
import { authState } from "@/atoms/auth";
import { Receipt, Transaction } from "@/types/entities";
import { createTransaction, transactionsState } from "@/atoms/transaction";
import { createReceipt, receiptsState } from "@/atoms/receipt";
import { useNavigate } from "react-router-dom";
import { assetsState, createAsset } from "@/atoms/receipt_asset";
import { processFile } from "./fileProcessing";
import { uploadFileAndGetJsonResponse } from "./uploadHandler";
import { useToast } from "@/components/ui/use-toast";
import { fetchProfileStats, profileStatsState } from "@/atoms/profile_stats";

export const useAnalyzeReceipt = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isAnalyzing, setIsAnalyzing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const setReceipts = useSetRecoilState(receiptsState);
  const setTransactions = useSetRecoilState(transactionsState);
  const setAssets = useSetRecoilState(assetsState);
  const [user] = useRecoilState(authState);
  const setProfileStats = useSetRecoilState(profileStatsState);
  const navigate = useNavigate();
  const { toast } = useToast();

  const startAnalysis = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files ? event.target.files[0] : null;
    if (!file) {
      setError("No file selected");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError("File size should not exceed 10 MB");
      return;
    }

    if (!user) {
      setError("User not authenticated");
      return;
    }

    setError(null);
    setIsLoading(true);
    setIsAnalyzing(false);

    try {
      const docKey = nanoid();

      const assetUploadPromise = createAsset(file, setAssets, docKey);
      const jsonResponsePromise = uploadFileAndGetJsonResponse(file, processFile);

      const { jsonResponse, responseTime } = await jsonResponsePromise;
      console.log('clearJsonResponse', jsonResponse);
      console.log("responseTime", responseTime);

      const analysisResult = { ...jsonResponse, source: "receipt_upload" };
      console.log("analysisResult", analysisResult);

      const newReceipt: Receipt = {
        ...analysisResult,
      };
      console.log("newReceipt", newReceipt);

      const receiptCreationPromise = createReceipt(newReceipt, setReceipts, docKey);

      const now = new Date();
      const localOffset = now.getTimezoneOffset()

      const transaction: Transaction = {
        date: analysisResult.date,
        name: analysisResult.store_name,
        transaction_type: "expense",
        is_recurring: false,
        amount_cents: Math.round(analysisResult.total_price * 100),
        amount_currency: analysisResult.locale.currency,
        category: analysisResult.main_category,
        receipt_id: docKey,
        profile_id: user.key,
        offset: localOffset * 60
      };

      const transactionCreationPromise = createTransaction(transaction, setTransactions, docKey);

      // Wait for all tasks to complete
      await Promise.all([assetUploadPromise, receiptCreationPromise, transactionCreationPromise]);

      toast({
        title: "Upload successful!",
        description: "Your receipt has been uploaded successfully.",
      });

      console.log("Upload success");

      // Fetch updated profile stats
      console.log("Fetching updated profile stats...");
      const updatedStats = await fetchProfileStats(user.key);
      if (updatedStats) {
        console.log("Updated profile stats:", updatedStats);

        // Calculate the new total_items count
        const newTotalItems = updatedStats.total_items + newReceipt.line_items.reduce((total, item) => total + item.quantity, 0);
        const updatedProfileStats = {
          ...updatedStats,
          total_items: newTotalItems,
        };
        console.log("Updating profile stats with new total_items:", updatedProfileStats);

        // Update the Recoil state with the new profile stats
        setProfileStats(updatedProfileStats);
      } else {
        console.log("Failed to fetch updated profile stats.");
      }

      // Clear the error state after successful upload
      setError(null);

      navigate('/');
    } catch (error) {
      console.error(
        "We failed to recognize your receipt. Please try taking another picture",
        error
      );
      setError(
        "We failed to recognize your receipt. Please try taking another picture."
      );
    } finally {
      setIsAnalyzing(false);
      setIsLoading(false);
    }
  };

  return {
    startAnalysis,
    isLoading,
    isAnalyzing,
    error,
  };
};